import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, Lock, Crown, Zap, Target } from "lucide-react";
import { useOrganizationStore } from "@/store/useOrganizationStore";

export function CustomerVisitTrendLockView() {
  const organization = useOrganizationStore((state) => state.organization);

  const handleUpgrade = () => {
    window.open(
      `https://clover.com/appmarket/m/${organization.referenceId}/apps/${organization.appReferenceId}`,
      "_blank"
    );
  };

  return (
    <Card className="relative overflow-hidden">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          Customer Visit Trend
        </CardTitle>
        <p className="text-gray-600 text-sm">
          Track customer visit patterns and engagement frequency over time.
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Chart Preview with Real Image */}
        <div className="h-64 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg flex items-center justify-center">
            <img
              src="/customerVisitTrendBarChart.png"
              alt="Customer Visit Trend Chart Preview"
              className="max-w-full max-h-full object-contain opacity-60"
            />
          </div>
          <div className="absolute inset-0 backdrop-blur-sm bg-white/30"></div>
        </div>

        {/* Premium Chart Preview Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Lock className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Premium Analytics Preview
              </p>
              <p className="text-xs text-blue-700">
                Unlock to view interactive customer visit trend analytics
              </p>
            </div>
          </div>
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            onClick={handleUpgrade}
          >
            <Crown className="h-4 w-4" />
            Upgrade
          </Button>
        </div>

        {/* Key Benefits */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4 text-yellow-500" />
            <h4 className="text-sm font-semibold text-gray-900">
              Key Benefits
            </h4>
          </div>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
              <span>
                Track customer visit patterns and engagement frequency
              </span>
            </li>
            <li className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
              <span>Identify peak customer activity periods</span>
            </li>
            <li className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
              <span>Monitor customer engagement trends</span>
            </li>
          </ul>
        </div>

        {/* Business Decisions */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Target className="h-4 w-4 text-green-500" />
            <h4 className="text-sm font-semibold text-gray-900">
              Business Decisions
            </h4>
          </div>
          <ul className="space-y-2 text-sm text-gray-500">
            <li className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
              <span>
                Optimize customer engagement strategies based on visit patterns
              </span>
            </li>
            <li className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
              <span>Plan marketing campaigns during peak activity periods</span>
            </li>
            <li className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
              <span>
                Design targeted re-engagement campaigns for inactive customers
              </span>
            </li>
            <li className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
              <span>Enhance customer experience based on visit behavior</span>
            </li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
